import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { Link } from 'react-router-dom';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';

const Row = ({
  item,
  index,
  selected,
  onSelect,
  id,
  columns,
  enableDnd = false,
  enableCheckbox = false,
  selectedCount = 0,
  showOrderNumber = true,
  isDragOverlay = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: enableDnd && !isDragOverlay ? id : undefined });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={enableDnd && !isDragOverlay ? setNodeRef : undefined}
      style={enableDnd && !isDragOverlay ? style : undefined}
      className={`${
        selected ? 'bg-blue-100 dark:bg-gray-600' : 'bg-transparent'
      } hover:bg-gray-200 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 dark:text-gray-200 ${
        isDragOverlay ? 'shadow-lg' : ''
      }`}
    >
      {enableDnd && (
        <td
          className="p-2 text-center text-sm cursor-move w-8"
          {...(isDragOverlay ? {} : { ...attributes, ...listeners })}
        >
          <span className="flex flex-row justify-center items-center -space-x-3 text-gray-400">
            <EllipsisVerticalIcon className="w-5 h-5" />
            <EllipsisVerticalIcon className="w-5 h-5" />
          </span>
        </td>
      )}

      {enableCheckbox && (
        <td className="p-2 text-center w-8">
          <input
            type="checkbox"
            aria-label={`Select ${item.question?.name || item.name || item.title || `row ${index + 1}`}`}
            checked={selected}
            onChange={() => !isDragOverlay && onSelect(id)}
            className="accent-blue-600 w-4 h-4 rounded-md cursor-pointer"
          />
        </td>
      )}

      {showOrderNumber && (
        <td className="p-2 text-center text-sm w-10">{index + 1}</td>
      )}

      {columns.map((column, colIndex) => (
        <td
          key={colIndex}
          className={`p-2 text-sm ${column.className || ''} ${
            column.truncate ? 'truncate' : ''
          }`}
          style={column.width ? { width: column.width } : {}}
        >
          {column.render ? column.render(item, index) : item[column.key]}
        </td>
      ))}
    </tr>
  );
};

const MultiDragOverlay = ({
  items,
  columns,
  enableCheckbox,
  showOrderNumber,
  selectedCount,
}) => {
  return (
    <div className="bg-transparent rounded-md shadow-xl border-2 border-blue-300 overflow-hidden">
      <table className="w-full border-collapse">
        <tbody>
          {items.map((item, index) => (
            <Row
              key={item.uuid}
              id={item.uuid}
              item={item}
              index={index}
              selected={true}
              onSelect={() => {}}
              columns={columns}
              enableDnd={true}
              enableCheckbox={enableCheckbox}
              selectedCount={selectedCount}
              showOrderNumber={showOrderNumber}
              isDragOverlay={true}
            />
          ))}
        </tbody>
      </table>
      <div className="bg-blue-500 text-white text-xs px-2 py-1 text-center">
        Moving {items.length} items
      </div>
    </div>
  );
};

const FullTable = ({
  data = [],
  columns = [],
  enableSearch = true,
  enableCheckbox = true,
  enableDnd = false,
  searchFields = [],
  searchPlaceholder = 'Search...',
  actionButtonsTable = [],
  actionButtons = [],
  onSelectionChange,
  onReorder,
  loading = false,
  error = null,
  className = '',
  emptyMessage = 'No data available',
  showOrderNumber = true,
}) => {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeId, setActiveId] = useState(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Vertical-only drag sensor
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  useEffect(() => {
    setSelectedItems([]);
  }, [enableCheckbox]);

  // Handle data updates
  useEffect(() => {
    setItems(data);
  }, [data]);

  // Handle search filtering
  useEffect(() => {
    if (!enableSearch || !debouncedSearchTerm.trim()) {
      setItems(data);
    } else {
      const filtered = data.filter((item) =>
        searchFields.some((field) =>
          item[field]
            ?.toString()
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase()),
        ),
      );
      setItems(filtered);
    }
  }, [debouncedSearchTerm, data, searchFields, enableSearch]);

  // Selection handlers - Use item UUIDs
  const handleSelect = (uuid) => {
    if (!enableCheckbox) return;
    const newSelection = selectedItems.includes(uuid)
      ? selectedItems.filter((item) => item !== uuid)
      : [...selectedItems, uuid];
    setSelectedItems(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleSelectAll = () => {
    if (!enableCheckbox) return;
    const newSelection =
      selectedItems.length === items.length
        ? []
        : items.map((item) => item.uuid);
    setSelectedItems(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Helper function to find insert position for multi-select drag
  const findInsertPosition = (
    items,
    selectedItems,
    oldIndex,
    newIndex,
    nonSelectedItems,
  ) => {
    const targetItem = items[newIndex];
    const targetIndexInNonSelected = nonSelectedItems.findIndex(
      (item) => item.uuid === targetItem.uuid,
    );

    // If target is non-selected, insert before or after based on direction
    if (targetIndexInNonSelected !== -1) {
      return newIndex > oldIndex
        ? targetIndexInNonSelected + 1
        : targetIndexInNonSelected;
    }

    // Target is selected, find closest non-selected item
    const isMovingDown = newIndex > oldIndex;

    if (isMovingDown) {
      // Find next non-selected item after newIndex
      for (let i = newIndex + 1; i < items.length; i++) {
        const index = nonSelectedItems.findIndex(
          (item) => item.uuid === items[i].uuid,
        );
        if (index !== -1) return index;
      }
      return nonSelectedItems.length; // Insert at end
    } else {
      // Find previous non-selected item before newIndex
      for (let i = newIndex - 1; i >= 0; i--) {
        const index = nonSelectedItems.findIndex(
          (item) => item.uuid === items[i].uuid,
        );
        if (index !== -1) return index + 1;
      }
      return 0; // Insert at beginning
    }
  };

  const handleDragEnd = (event) => {
    if (!enableDnd) return;

    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.uuid === active.id);
    const newIndex = items.findIndex((item) => item.uuid === over.id);

    // Single item drag - simple case
    const isMultiSelect =
      selectedItems.includes(active.id) && selectedItems.length > 1;

    if (!isMultiSelect) {
      const reorderedItems = arrayMove(items, oldIndex, newIndex);
      setItems(reorderedItems);
      onReorder?.(reorderedItems);
      return;
    }

    // Multi-select drag - move all selected items together
    const selectedItemsData = selectedItems
      .map((uuid) => items.find((item) => item.uuid === uuid))
      .filter(Boolean);

    const nonSelectedItems = items.filter(
      (item) => !selectedItems.includes(item.uuid),
    );

    const insertIndex = findInsertPosition(
      items,
      selectedItems,
      oldIndex,
      newIndex,
      nonSelectedItems,
    );

    const reorderedItems = [
      ...nonSelectedItems.slice(0, insertIndex),
      ...selectedItemsData,
      ...nonSelectedItems.slice(insertIndex),
    ];

    setItems(reorderedItems);
    onReorder?.(reorderedItems, newIndex, selectedItemsData);
  };

  const isLoading = loading;
  const currentError = error;

  if (currentError) {
    return (
      <div
        className={`bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded ${className}`}
      >
        {currentError}
      </div>
    );
  }

  // Get active item and selected items for drag overlay
  const activeItem = items.find((item) => item.uuid === activeId);
  const activeSelectedItems =
    selectedItems.includes(activeId) && selectedItems.length > 1
      ? selectedItems
          .map((uuid) => items.find((item) => item.uuid === uuid))
          .filter(Boolean)
      : activeItem
      ? [activeItem]
      : [];

  const TableContent = () => (
    <div className="rounded-md border border-gray-200 bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 shadow-sm overflow-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="font-semibold text-sm text-gray-600 bg-gray-200 border-b border-gray-200 dark:bg-gray-700 dark:border-gray-700 dark:text-gray-200">
            {enableDnd && <th className="p-2 w-8"></th>}
            {enableCheckbox && (
              <th className="p-2 text-center w-8">
                <input
                  type="checkbox"
                  aria-label="Select all rows"
                  checked={
                    items.length > 0 && selectedItems.length === items.length
                  }
                  onChange={handleSelectAll}
                  className="accent-blue-600 w-4 h-4 rounded-md cursor-pointer"
                />
              </th>
            )}
            {showOrderNumber && <th className="p-2 text-center w-10 ">No.</th>}
            {columns.map((column, index) => (
              <th
                key={index}
                className={`p-2 text-left ${column.width ? 'w-max' : ''} ${
                  column.headerClassName || ''
                }`}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td
                colSpan={
                  columns.length +
                  (enableDnd ? 1 : 0) +
                  (enableCheckbox ? 1 : 0) +
                  (showOrderNumber ? 1 : 0)
                }
                className="p-8 text-center text-gray-500"
              >
                Loading...
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td
                colSpan={
                  columns.length +
                  (enableDnd ? 1 : 0) +
                  (enableCheckbox ? 1 : 0) +
                  (showOrderNumber ? 1 : 0)
                }
                className="p-8 text-center text-gray-500"
              >
                {debouncedSearchTerm ? 'No items found' : emptyMessage}
              </td>
            </tr>
          ) : (
            items.map((item, index) => (
              <Row
                key={item.uuid}
                id={item.uuid}
                item={item}
                index={index}
                selected={selectedItems.includes(item.uuid)}
                onSelect={handleSelect}
                columns={columns}
                enableDnd={enableDnd}
                enableCheckbox={enableCheckbox}
                selectedCount={selectedItems.length}
                showOrderNumber={showOrderNumber}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="flex w-full gap-4 items-center">
        {/* Search Bar */}
        {enableSearch && (
          <div className="relative w-full">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 text-sm placeholder:text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        )}

        {actionButtons.map((btn, index) =>
          btn.link ? (
            <Link
              to={btn.link}
              className={`px-4 py-2 h-full whitespace-nowrap text-sm rounded ${
                btn.className || 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {btn.label}
            </Link>
          ) : (
            <button
              key={index}
              onClick={btn.onClick}
              className={`px-4 py-2 h-full whitespace-nowrap text-sm rounded ${
                btn.className || 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {btn.label}
            </button>
          ),
        )}
      </div>

      {/* Controls */}
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-600">
          {isLoading ? 'Loading...' : `${items.length} items`}
          {selectedItems.length > 0 && ` • ${selectedItems.length} selected`}
        </span>

        <div className="flex gap-2">
          {selectedItems.length > 0 && (
            <button
              onClick={() => {
                setSelectedItems([]);
                onSelectionChange?.([]);
              }}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear
            </button>
          )}
          {actionButtonsTable.map((button, index) => (
            <button
              key={index}
              onClick={() => button.onClick(selectedItems, items)}
              disabled={button.requiresSelection && selectedItems.length === 0}
              className={`px-3 py-1 text-sm rounded ${
                button.className || 'bg-blue-500 text-white hover:bg-blue-600'
              } disabled:bg-gray-300`}
            >
              {button.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="w-full overflow-auto">
        {enableDnd ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((item) => item.uuid)}
              strategy={verticalListSortingStrategy}
            >
              <TableContent />
            </SortableContext>
            <DragOverlay>
              {activeSelectedItems.length > 1 ? (
                <MultiDragOverlay
                  items={activeSelectedItems}
                  columns={columns}
                  enableCheckbox={enableCheckbox}
                  showOrderNumber={showOrderNumber}
                  selectedCount={activeSelectedItems.length}
                />
              ) : activeItem ? (
                <div className="bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 rounded-md shadow-xl border border-gray-300 overflow-hidden">
                  <table className="w-full border-collapse">
                    <tbody>
                      <Row
                        key={activeItem.uuid}
                        id={activeItem.uuid}
                        item={activeItem}
                        index={0}
                        selected={selectedItems.includes(activeItem.uuid)}
                        onSelect={() => {}}
                        columns={columns}
                        enableDnd={true}
                        enableCheckbox={enableCheckbox}
                        selectedCount={selectedItems.length}
                        showOrderNumber={showOrderNumber}
                        isDragOverlay={true}
                      />
                    </tbody>
                  </table>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <TableContent />
        )}
      </div>
    </div>
  );
};

export default FullTable;
